import { Trash2, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";

const iconComponents: any = {
  delete: Trash2,
  warning: AlertTriangle,
  success: CheckCircle,
};

const iconColors: any = {
  delete: "text-red-500",
  warning: "text-red-500", // Changed to red to match image
  success: "text-green-500",
};

const buttonBaseColors: any = {
  delete: "bg-red-600 hover:bg-red-700 text-white",
  warning: "bg-red-600 hover:bg-red-700 text-white", // Changed to red
  success: "bg-green-600 hover:bg-green-700 text-white",
};

const iconBackgroundColors: any = {
  delete: "bg-red-50",
  warning: "bg-red-50", // Changed to red background
  success: "bg-green-100",
};

type ButtonStyle = "fill" | "outline";

type ConfirmationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  iconName?: "delete" | "warning" | "success";
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonStyle?: ButtonStyle;
  cancelButtonStyle?: ButtonStyle;
};

const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  iconName = "delete",
  title = "Confirm Action",
  description = "Are you sure you want to proceed? This action cannot be undone.",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmButtonStyle = "fill",
  cancelButtonStyle = "fill",
}: ConfirmationDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  if (!isOpen) return null;

  const IconComponent = iconComponents[iconName];

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm(); 
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: any) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getConfirmButtonStyle = (buttonStyle: ButtonStyle) => {
    const baseColors = buttonBaseColors[iconName] || "";
    if (buttonStyle === "outline") {
      return `flex-1 py-2.5 px-4 rounded-lg border ${
        iconColors[iconName]
      } ${baseColors.replace(
        "text-white",
        ""
      )} transition-colors font-medium text-sm`;
    } else {
      return `flex-1 py-2.5 px-4 rounded-lg transition-colors font-medium text-sm ${baseColors}`;
    }
  };

  const getCancelButtonStyle = (buttonStyle: ButtonStyle) => {
    if (buttonStyle === "outline") {
      return `flex-1 py-2.5 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors font-medium text-sm`;
    } else {
      return `flex-1 py-2.5 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors font-medium text-sm`;
    }
  };

  const IconBackgroundColor = iconBackgroundColors[iconName];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Content */}
        <div className="px-6 py-6 text-center">
          {IconComponent && (
            <div
              className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-3 ${IconBackgroundColor}`}
            >
              <IconComponent className={`w-5 h-5 ${iconColors[iconName]}`} />
            </div>
          )}

          <h2 className="text-lg font-semibold mb-2 text-gray-900">{title}</h2>

          <p
            className="text-sm text-gray-600 mb-6 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: description }}
          />

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={getCancelButtonStyle(cancelButtonStyle)}
            >
              {cancelLabel}
            </button>

            <button
              onClick={handleConfirm}
              className={`${getConfirmButtonStyle(confirmButtonStyle)} relative flex justify-center items-center`}
              disabled={isLoading}
            >
              {isLoading ? ( <div className="flex items-center gap-2 text-center"> <Loader2 className="w-4 h-4 animate-spin" /> <span>{confirmLabel}</span> </div> ) : ( <span>{confirmLabel}</span> )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
